import React, { useEffect, useRef, useState } from 'react';
import CompanyChip from './CompanyChip';
import '../Assets.css';

/**
 * The company filter on the register's list: tick any number of companies,
 * or "No company". Reads like the selects beside it - "Company", or "2
 * companies" once something is ticked - and opens a panel of tick-boxes
 * with the count of devices against each, when the list has sent one.
 *
 * `value` is the chosen ids, 'none' among them for devices with no company.
 */
export default function CompanyFilter({ companies, value, counts = null, onChange }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const key = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', key); };
  }, [open]);

  const chosen = new Set(value || []);
  const toggle = (id) => {
    const next = new Set(chosen);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange([...next]);
  };
  const countOf = (id) => {
    if (!counts) return null;
    const row = counts.find((c) => (id === 'none' ? c.id === null : c.id === id));
    return row ? row.n : 0;
  };
  const live = (companies || []).filter((c) => !c.archived_at || chosen.has(c.id));
  const label = chosen.size === 0 ? 'Every company'
    : chosen.size === 1 ? (chosen.has('none') ? 'No company' : (companies.find((c) => c.id === [...chosen][0])?.name || '1 company'))
      : `${chosen.size} companies`;

  return (
    <div className="as-multi" ref={box}>
      <button type="button" className="as-multi-btn" data-on={String(chosen.size > 0)} aria-haspopup="true"
        aria-expanded={open} aria-label="Company" onClick={() => setOpen(!open)}>
        <span>{label}</span>
      </button>
      {open && (
        <div className="as-multi-panel" role="group" aria-label="Companies">
          {live.map((c) => (
            <label key={c.id} className="as-check">
              <input type="checkbox" checked={chosen.has(c.id)} onChange={() => toggle(c.id)} />
              <CompanyChip name={c.name} color={c.color} />
              {counts && <span className="as-count">{countOf(c.id)}</span>}
            </label>
          ))}
          <label className="as-check">
            <input type="checkbox" checked={chosen.has('none')} onChange={() => toggle('none')} />
            <CompanyChip name="No company" none="No company" />
            {counts && <span className="as-count">{countOf('none')}</span>}
          </label>
          <div className="as-multi-foot">
            <button type="button" className="btn btn-plain btn-small" disabled={chosen.size === 0} onClick={() => onChange([])}>Clear</button>
            <button type="button" className="btn btn-secondary btn-small" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
